import { Button } from 'component/Button'
import { Spinner } from 'component/Spinner'
import { Space } from 'component/Space'
import { Typo } from 'component/Typo/Typo'
import { Vertical } from 'component/Vertical'
import { i18n } from 'i18n'
import { observer } from 'mobx-react-lite'
import { useDemo } from 'page/catalog/store/CatalogContext'
import { comboCartStore } from 'page/catalog/store/ComboCartStore'
import { useCallback, useEffect, useState } from 'react'
import { Item } from 'type/Item'
import { ComboCartColumn } from './ComboCartColumn'
import css from './ComboCartMobile.module.scss'
import { ComboCartSteps } from './ComboCartSteps'

interface ComboCartMobileProps {
  onCombine: () => void
  onFiltersCountChange?: (count: number) => void
  onStepChange?: (step: 'top' | 'bottom') => void
  isGenerating?: boolean
}

function getName(item: Item | null, show_name: boolean, top: boolean) {
  const { product_name } = item ?? {}
  const name = show_name ? product_name : ''
  return name || (top ? i18n('DressShapeTop') : i18n('DressShapeBottom'))
}

export const ComboCartMobile = observer(
  ({ onCombine, onFiltersCountChange, onStepChange, isGenerating = false }: ComboCartMobileProps) => {
    const [currentStep, setCurrentStep] = useState<'top' | 'bottom'>('top')
    const demo = useDemo()
    const { show_name, product, options } = demo

    const handleStepChange = (step: 'top' | 'bottom') => {
      setCurrentStep(step)
      if (onStepChange) {
        onStepChange(step)
      }
    }

    const handleContinue = () => {
      if (comboCartStore.selectedTop) {
        handleStepChange('bottom')
      }
    }

    const handleCombine = () => {
      if (comboCartStore.canCombine) {
        onCombine()
      }
    }

    const handleFiltersCountChange = useCallback(
      (count: number) => {
        if (onFiltersCountChange) {
          onFiltersCountChange(count)
        }
      },
      [onFiltersCountChange],
    )

    useEffect(() => {
      if (onFiltersCountChange) {
        if (currentStep === 'top') {
          const { brands, colors } = comboCartStore.topFilters
          onFiltersCountChange(
            brands.value.length + colors.value.length,
          )
        } else {
          const { brands, colors } = comboCartStore.bottomFilters
          onFiltersCountChange(
            brands.value.length + colors.value.length,
          )
        }
      }
    }, [currentStep, onFiltersCountChange])

    const canCombine = comboCartStore.canCombine
    const hasSelectedTop = !!comboCartStore.selectedTopItem

    const renderSelectedItems = () => {
      const topItem = getName(comboCartStore.selectedTopItem, show_name, true)
      const bottomItem = getName(comboCartStore.selectedBottomItem, show_name, false)

      if (
        !comboCartStore.selectedTopItem &&
        !comboCartStore.selectedBottomItem
      ) {
        return null
      }

      return (
        <Space gap={8} wide center>
          <Typo size={11} ellipsis>
            {topItem}
          </Typo>{' '}
          <Typo size={11} secondary>
            +
          </Typo>{' '}
          <Typo size={11} ellipsis>
            {bottomItem}
          </Typo>
        </Space>
      )
    }

    return (
      <Vertical className={css.mobileContainer}>
        <ComboCartSteps
          currentStep={currentStep}
          onStepChange={handleStepChange}
        />

        <div className={css.content}>
          {currentStep === 'top' && (
            <ComboCartColumn
              title={i18n('combo.Top')}
              items={comboCartStore.topItems}
              selectedItemId={comboCartStore.selectedTop}
              onSelectItem={(itemId) => comboCartStore.selectTop(itemId)}
              onRemoveItem={(itemId) => comboCartStore.removeItem(itemId)}
              emptyMessage={i18n('combo.NoTopItems')}
              options={options}
              product={product}
              onFiltersCountChange={handleFiltersCountChange}
              showFilters={false}
              filterType="top"
            />
          )}

          {currentStep === 'bottom' && (
            <ComboCartColumn
              title={i18n('combo.Bottom')}
              items={comboCartStore.bottomItems}
              selectedItemId={comboCartStore.selectedBottom}
              onSelectItem={(itemId) => comboCartStore.selectBottom(itemId)}
              onRemoveItem={(itemId) => comboCartStore.removeItem(itemId)}
              emptyMessage={i18n('combo.NoBottomItems')}
              options={options}
              product={product}
              onFiltersCountChange={handleFiltersCountChange}
              showFilters={false}
              filterType="bottom"
            />
          )}
        </div>

        <Vertical className={css.footer} gap={16}>
          {currentStep === 'top' && (
            <>
              {renderSelectedItems()}
              <Button
                wide
                xl
                primary
                action={handleContinue}
                disabled={!hasSelectedTop}
              >
                {i18n('combo.Continue')}
              </Button>
            </>
          )}

          {currentStep === 'bottom' && (
            <>
              {renderSelectedItems()}
              <Button
                wide
                xl
                primary
                action={handleCombine}
                disabled={!canCombine || isGenerating}
              >
                <Space gap={8}>
                  {isGenerating && <Spinner size={16} white />}
                  {isGenerating ? i18n('combo.GeneratingOutfit') : i18n('combo.Combine')}
                </Space>
              </Button>
            </>
          )}
        </Vertical>
      </Vertical>
    )
  },
)
